import { Activity, AlertCircle } from "../../../../components/icons";
import type { CardSelection } from "./QuickCardPanel";

interface RefereeActionBarProps {
  disabled: boolean;
  /**
   * @deprecated PMA-COCK-118 — the 5 neutral referee-reason buttons this
   * bar used to render ("Cambiar equipo (CSH)"/switch-ineffective-team,
   * "Balón golpea al árbitro / interferencia", "Discusión / explicación
   * arbitral", "Lesión arbitral", "Problema de equipo / comunicación")
   * collapsed into the existing `btn-timeout-toggle` control
   * (`MatchTimerDisplay.tsx`) at the client's explicit request. This was
   * blocked until the backend started aggregating `TimeoutStart`/
   * `TimeoutStop` (commit ad9f351) — before that, collapsing into the
   * timeout button would have silently dropped neutral stoppage time
   * instead of counting it. Task #42 (PMA-COCK-118 follow-up): the
   * "future caller" this deprecation was kept for never arrived —
   * `switchIneffectiveTeam` itself has been deleted from
   * `useIneffectiveTime.ts` and its whole prop-threading chain
   * (`CockpitContext.tsx` → `LoggerCockpit.tsx` → `LoggerView.tsx`) as
   * confirmed dead code. Same root-cause lesson as PMA-COCK-120's
   * `applyFormation`/`bench-area` deletion: a "kept for a future caller"
   * deprecation is itself the repeat pattern, not a safe default.
   */
  switchDisabled?: boolean;
  onActionSelect: (actionId: string) => void;
  /** @deprecated PMA-COCK-118 — see `switchDisabled` above. */
  hasActiveIneffective?: boolean;
  /** @deprecated PMA-COCK-118 — see `switchDisabled` above. */
  ineffectiveTeamLabel?: string;
  homeTeamShortName?: string;
  awayTeamShortName?: string;
  /** PMA-COCK-116 (client request — "cuadritos" in the freed control area):
   * one-tap yellow/red card arming, matching the bench row's compact visual
   * language. Calls the SAME handler the existing `QuickCardPanel` uses —
   * not a new mechanism — so behavior (team resolved from the next-tapped
   * player) is identical to that pre-existing entry point.
   *
   * PMA-COCK-207 (EPIC-61, client wave 2026-09-19, brief 6): the sibling
   * `onSubstitution` prop this row used to also carry (`ref-bar-sub-home`/
   * `ref-bar-sub-away`) is deleted, not merely unused — see the removed
   * buttons' own note below and this codebase's own PMA-COCK-118 lesson
   * ("kept for a future caller" is the repeat pattern, not a safe
   * default). The rail's real substitution entry point is the pair under
   * the pitch (`QuickSubstitutionPanel`, mounted from `ActionStage.tsx`,
   * a different call site, untouched by this change). */
  onCardSelect: (card: "Yellow" | "Red") => void;
  activeCard?: CardSelection | null;
  /** Cards stay available through a clock stoppage (same
   * gating `QuickCardPanel`/`QuickSubstitutionPanel` already use at their
   * `ActionStage.tsx` call sites — `disabled={cockpitLocked}`, NOT gated on
   * `clockMode`) — deliberately NOT the same as this component's own
   * `disabled` prop above, which also blocks on `clockMode !== "EFFECTIVE"`
   * for the neutral-reason/timeout actions specifically. */
  cardsAndSubDisabled?: boolean;
  t: any;
  /** PMA-COCK-106 (3rd revision): true when rendered inside the landscape-
   * tablet rail (a ~200px column). This component's row/grid layout
   * switches on Tailwind's `lg:` breakpoint — a VIEWPORT media query that
   * defaults to >=1024px — from stacked to row and from 2 to 5 grid
   * columns. At exactly the 1024px viewport this rail applies at, `lg:`
   * fires regardless of the rail's actual ~200px width, cramming a
   * 5-column button grid into a container that cannot hold it. Same
   * container-vs-viewport bug already fixed on `MatchTimerDisplay.tsx` and
   * recorded on `ScoreBoard.tsx`/`TacticalField.tsx`. `isRailLayout` forces
   * a single stacked column unconditionally, independent of viewport
   * width. */
  isRailLayout?: boolean;
}

/**
 * @deprecated PMA-COCK-118 — kept for a future caller, not rendered. See
 * the `switchDisabled` doc block on `RefereeActionBarProps` above. Exported
 * (rather than left module-private) purely so it isn't flagged unused now
 * that nothing in this file renders it.
 */
export const REFEREE_ACTIONS = [
  {
    id: "interference",
    labelKey: "refereeActionInterference",
    fallback: "Ball hits referee / interference",
  },
  {
    id: "discussion",
    labelKey: "refereeActionDiscussion",
    fallback: "Referee discussion / explanation",
  },
  {
    id: "injury",
    labelKey: "refereeActionInjury",
    fallback: "Referee injury",
  },
  {
    id: "equipment",
    labelKey: "refereeActionEquipment",
    fallback: "Equipment / communication issue",
  },
];

export default function RefereeActionBar({
  disabled,
  onActionSelect,
  homeTeamShortName,
  awayTeamShortName,
  onCardSelect,
  activeCard = null,
  cardsAndSubDisabled = false,
  t,
  isRailLayout = false,
}: RefereeActionBarProps) {
  return (
    <section
      className="rounded-xl border border-amber-500/30 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-4 py-3 shadow-lg shadow-slate-950/20"
      data-testid="referee-action-bar"
    >
      <div className="flex flex-col gap-3">
        {/* ── Row 2 (PMA-COCK-207, EPIC-61 brief 6 — cards moved above
            team injury, "most used" per the client): quick card tiles,
            compact, not the bench row's numbered-circle look (nothing
            player-specific here), but same rounded-md / non-token shape
            and touch-target sizing. Calls the SAME `onCardSelect` handler
            `QuickCardPanel` already uses elsewhere in the flow — an
            additional entry point, not a new mechanism. The substitution
            tiles that used to sit alongside these (`ref-bar-sub-home`/
            `ref-bar-sub-away`) are deleted, not hidden — see the prop doc
            block above. ── */}
        {/* ── Active-card contrast (PMA-COCK-207 AC4, T7's wave-wide
            computed WCAG audit, EPIC-61): the ARMED state's fill was
            `yellow-600`/`red-600` against `yellow-50`/`red-50` text —
            2.84:1 and 4.41:1, both below the 4.5:1 floor for text-xs
            (12px, below the 18.66px-bold "large text" threshold). This is
            the state the operator sees immediately after tapping a card,
            so it is squarely in scope even though the colours predate this
            wave (PMA-COCK-116). Fixed by darkening the fill one step
            (`-600` to `-700`) rather than swapping the colour family or the
            text token — `yellow-50` on `yellow-700` computes 4.76:1,
            `red-50` on `red-700` computes 5.91:1 (WCAG relative-luminance
            formula, same method T7 used elsewhere in this wave), both
            clear with margin, and an armed yellow/red card still reads as
            yellow/red. Inactive-state colours (`-500/15` fills) are
            unchanged — not flagged by the audit, out of scope. ── */}
        {isRailLayout ? (
          <div className="grid grid-cols-2 gap-2">
            <span className="col-span-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {t("quickActionTitle", "Quick actions")}
            </span>
            <button
              type="button"
              data-testid="ref-bar-card-yellow"
              onClick={() => onCardSelect("Yellow")}
              disabled={cardsAndSubDisabled}
              className={`inline-flex items-center justify-center gap-1.5 min-h-11 px-1.5 rounded-md text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                activeCard === "Yellow"
                  ? "bg-yellow-700 text-yellow-50 border border-yellow-300/70"
                  : "bg-yellow-500/15 text-yellow-100 border border-yellow-400/30 hover:bg-yellow-500/25"
              }`}
            >
              <AlertCircle size={14} className="shrink-0 text-yellow-300" />
              <span className="truncate">
                {t("cardSelectYellow", "Yellow")}
              </span>
            </button>
            <button
              type="button"
              data-testid="ref-bar-card-red"
              onClick={() => onCardSelect("Red")}
              disabled={cardsAndSubDisabled}
              className={`inline-flex items-center justify-center gap-1.5 min-h-11 px-1.5 rounded-md text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                activeCard === "Red"
                  ? "bg-red-700 text-red-50 border border-red-300/70"
                  : "bg-red-500/15 text-red-100 border border-red-400/30 hover:bg-red-500/25"
              }`}
            >
              <AlertCircle size={14} className="shrink-0 text-red-300" />
              <span className="truncate">{t("cardSelectRed", "Red")}</span>
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mr-1">
              {t("quickActionTitle", "Quick actions")}
            </span>
            <button
              type="button"
              data-testid="ref-bar-card-yellow"
              onClick={() => onCardSelect("Yellow")}
              disabled={cardsAndSubDisabled}
              className={`inline-flex items-center justify-center gap-1.5 min-h-11 px-3 rounded-md text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                activeCard === "Yellow"
                  ? "bg-yellow-700 text-yellow-50 border border-yellow-300/70"
                  : "bg-yellow-500/15 text-yellow-100 border border-yellow-400/30 hover:bg-yellow-500/25"
              }`}
            >
              <AlertCircle size={14} className="text-yellow-300" />
              {t("cardSelectYellow", "Yellow")}
            </button>
            <button
              type="button"
              data-testid="ref-bar-card-red"
              onClick={() => onCardSelect("Red")}
              disabled={cardsAndSubDisabled}
              className={`inline-flex items-center justify-center gap-1.5 min-h-11 px-3 rounded-md text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                activeCard === "Red"
                  ? "bg-red-700 text-red-50 border border-red-300/70"
                  : "bg-red-500/15 text-red-100 border border-red-400/30 hover:bg-red-500/25"
              }`}
            >
              <AlertCircle size={14} className="text-red-300" />
              {t("cardSelectRed", "Red")}
            </button>
          </div>
        )}

        {/* ── Row 1 (moved below cards, PMA-COCK-207): team-attributed
            injury buttons. PMA-COCK-123: rail branch densified to a
            2-col grid (label spans both columns, buttons paired) to
            remove a stacked row at iPad landscape; team name shortened to
            just the short code in the rail's ~85px cells (icon + full
            "Injury <team>" label clipped at that width — verified live,
            not assumed) with the full label preserved via `title` for
            hover/AT. Non-rail branch below is untouched byte-for-byte
            from pre-PMA-COCK-123 other than the border-t divider moving
            here with it. */}
        {isRailLayout ? (
          <div className="grid grid-cols-2 gap-2 border-t border-slate-700/50 pt-3">
            <span className="col-span-2 text-[10px] font-semibold uppercase tracking-wider text-rose-300/70">
              {t("teamInjuryLabel", "Team Injury")}
            </span>
            <button
              type="button"
              data-testid="team-injury-home"
              onClick={() => onActionSelect("injury-home")}
              disabled={disabled}
              title={`${t("teamInjuryButton", "Injury")} ${homeTeamShortName || t("homeTeam", "Home")}`}
              className="inline-flex min-h-11 items-center justify-center gap-1 rounded-lg border border-rose-500/30 bg-rose-500/10 px-1.5 py-2 text-xs font-semibold text-rose-100 transition-colors hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Activity size={14} className="shrink-0" />
              <span className="truncate">
                {homeTeamShortName || t("homeTeam", "Home")}
              </span>
            </button>
            <button
              type="button"
              data-testid="team-injury-away"
              onClick={() => onActionSelect("injury-away")}
              disabled={disabled}
              title={`${t("teamInjuryButton", "Injury")} ${awayTeamShortName || t("awayTeam", "Away")}`}
              className="inline-flex min-h-11 items-center justify-center gap-1 rounded-lg border border-rose-500/30 bg-rose-500/10 px-1.5 py-2 text-xs font-semibold text-rose-100 transition-colors hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Activity size={14} className="shrink-0" />
              <span className="truncate">
                {awayTeamShortName || t("awayTeam", "Away")}
              </span>
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2 border-t border-slate-700/50 pt-3">
            <span className="mr-auto text-[10px] font-semibold uppercase tracking-wider text-rose-300/70">
              {t("teamInjuryLabel", "Team Injury")}
            </span>
            <button
              type="button"
              data-testid="team-injury-home"
              onClick={() => onActionSelect("injury-home")}
              disabled={disabled}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-xs font-semibold text-rose-100 transition-colors hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Activity size={14} />
              <span>
                {t("teamInjuryButton", "Injury")}{" "}
                {homeTeamShortName || t("homeTeam", "Home")}
              </span>
            </button>
            <button
              type="button"
              data-testid="team-injury-away"
              onClick={() => onActionSelect("injury-away")}
              disabled={disabled}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-xs font-semibold text-rose-100 transition-colors hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Activity size={14} />
              <span>
                {t("teamInjuryButton", "Injury")}{" "}
                {awayTeamShortName || t("awayTeam", "Away")}
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
